import { FC, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipGroup } from '@/components/shared/tooltip/TooltipGroup';
import Button from '@/storybook/components/Button/Button';
import ScrollArea from '@/storybook/components/ScrollArea/ScrollArea';
import Text from '@/storybook/components/Text/Text';
import AILogoGradientIcon from '@/storybook/icons/AILogoGradientIcon';
import BetaBadge from '../BetaBadge/BetaBadge';
import { TestIds } from './testIds';
import styles from './FirstTimeDisclaimer.module.css';

export interface FirstTimeDisclaimerProps {
  onAccept: VoidFunction;
  onCancel?: VoidFunction;
}

const FirstTimeDisclaimer: FC<FirstTimeDisclaimerProps> = ({ onAccept, onCancel }) => {
  const { t } = useTranslation();
  const tooltipGroupId = useId();
  const disclaimerItems = [
    {
      title: t('components.aiChatDrawer.firstTimeDisclaimer.acceptableUseTitle'),
      body: t('components.aiChatDrawer.firstTimeDisclaimer.acceptableUseBody'),
    },
    {
      title: t('components.aiChatDrawer.firstTimeDisclaimer.aiGeneratedContentTitle'),
      body: t('components.aiChatDrawer.firstTimeDisclaimer.aiGeneratedContentBody'),
    },
    {
      title: t('components.aiChatDrawer.firstTimeDisclaimer.noMedicalAdviceTitle'),
      body: t('components.aiChatDrawer.firstTimeDisclaimer.noMedicalAdviceBody'),
    },
    {
      title: t('components.aiChatDrawer.firstTimeDisclaimer.noIndividualPerformanceEvaluationTitle'),
      body: t('components.aiChatDrawer.firstTimeDisclaimer.noIndividualPerformanceEvaluationBody'),
    },
  ];

  return (
    <TooltipGroup
      id={`ai-chat-disclaimer-${tooltipGroupId}`}
      shouldShowForTruncatedOnly={false}
      rootClassName={styles.tooltipGroup}
      childrenWrapperClassName={styles.tooltipGroupChildren}
      delayShow={200}
    >
      <ScrollArea
        scrollbars="y"
        className={styles.container}
        viewportProps={{
          'role': 'alertdialog',
          'aria-label': t('components.aiChatDrawer.firstTimeDisclaimer.ariaLabel'),
        }}
        data-testid={TestIds.container}
      >
        <div className={styles.hero}>
          <div className={styles.heroAvatar}>
            <AILogoGradientIcon className={styles.heroAvatarIcon} />
          </div>
          <div className={styles.heroInfo}>
            <Text as="span" className={styles.heroTitle}>
              {t('components.aiChatDrawer.header.title')}
            </Text>
            <BetaBadge data-testid={TestIds.betaBadge} />
          </div>
        </div>

        <div className={styles.conversationSection}>
          <div className={styles.response}>
            <div className={styles.bubble}>
              <div className={styles.copyBlock} data-testid={TestIds.text}>
                <AILogoGradientIcon className={styles.copyIcon} data-testid={TestIds.titleIcon} />
                <Text as="h2" className={styles.title} data-testid={TestIds.title}>
                  {t('components.aiChatDrawer.firstTimeDisclaimer.title')}
                </Text>

                <ul className={styles.bulletList}>
                  {disclaimerItems.map(({ title, body }) => (
                    <li key={title} className={styles.bulletItem}>
                      <Text as="span" className={styles.bulletTitle}>
                        {title}
                      </Text>{' '}
                      <Text as="span" className={styles.bulletBody}>
                        {body}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={styles.actions}>
                <Button
                  variant="outlined"
                  size="M"
                  onClick={onCancel}
                  className={styles.actionButton}
                  data-testid={TestIds.cancelButton}
                >
                  {t('components.aiChatDrawer.firstTimeDisclaimer.cancelButton')}
                </Button>
                <Button
                  variant="primary"
                  size="M"
                  onClick={onAccept}
                  className={styles.actionButton}
                  data-testid={TestIds.acceptButton}
                >
                  {t('components.aiChatDrawer.firstTimeDisclaimer.acceptButton')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </TooltipGroup>
  );
};

export default FirstTimeDisclaimer;
